import { useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useToast } from '../../shared/stores/toast.store';
import { describeApiError } from '../../shared/api/errors';
import { canonicalToBrl } from '../../shared/components/MoneyInput';
import { useCancelarRc, useEncerrarRc } from './hooks';

const OBS_MAX = 200;

export type EncerrarCancelarModo = 'encerrar' | 'cancelar';

export interface EncerrarCancelarDialogProps {
  open: boolean;
  purchaseRequestId: number;
  modo: EncerrarCancelarModo;
  /** Saldo atual (canônico) — mostrado como o valor que será debitado. */
  saldo: string;
  onClose: () => void;
}

/**
 * Encerrar/Cancelar RC — ações irreversíveis que criam um movimento de débito =
 * saldo (zera) e transicionam o status. Server computa o débito; o front só
 * confirma + observação (obrigatória). Card [vuaj4L1x].
 */
export function EncerrarCancelarDialog({
  open,
  purchaseRequestId,
  modo,
  saldo,
  onClose,
}: EncerrarCancelarDialogProps): JSX.Element {
  const { t } = useTranslation();
  const toast = useToast();
  const encerrar = useEncerrarRc(purchaseRequestId);
  const cancelar = useCancelarRc(purchaseRequestId);

  const [observacao, setObservacao] = useState('');
  const [touched, setTouched] = useState(false);

  const obsInvalida = observacao.trim().length === 0;
  const isPending = encerrar.isPending || cancelar.isPending;
  const prefix = `features.requisicoesCompra.encerrarCancelar.${modo}`;

  async function handleSubmit(): Promise<void> {
    setTouched(true);
    if (obsInvalida) return;
    const mut = modo === 'encerrar' ? encerrar : cancelar;
    try {
      await mut.mutateAsync({ observacao: observacao.trim() });
      toast.success(t(`${prefix}.toastOk`));
      onClose();
    } catch (err) {
      toast.error(describeApiError(err));
    }
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      TransitionProps={{
        onExited: () => {
          setObservacao('');
          setTouched(false);
        },
      }}
    >
      <DialogTitle>{t(`${prefix}.title`)}</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2}>
          <Alert severity="warning" variant="outlined">
            {t(`${prefix}.aviso`, { saldo: `R$ ${canonicalToBrl(saldo) || saldo}` })}
          </Alert>

          <Box>
            <Typography variant="caption" color="text.secondary" display="block">
              {t('features.requisicoesCompra.encerrarCancelar.saldoLabel')}
            </Typography>
            <Typography variant="h6" fontWeight={700}>
              R$ {canonicalToBrl(saldo) || saldo}
            </Typography>
          </Box>

          <TextField
            label={t('features.requisicoesCompra.encerrarCancelar.observacaoLabel')}
            value={observacao}
            onChange={(e) => setObservacao(e.target.value.slice(0, OBS_MAX))}
            onBlur={() => setTouched(true)}
            multiline
            minRows={2}
            maxRows={5}
            fullWidth
            error={touched && obsInvalida}
            helperText={
              touched && obsInvalida
                ? t('features.requisicoesCompra.encerrarCancelar.observacaoRequired')
                : t('features.requisicoesCompra.encerrarCancelar.observacaoHelper', {
                    used: observacao.trim().length,
                    max: OBS_MAX,
                  })
            }
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={isPending}>
          {t('common.cancel')}
        </Button>
        <Button
          variant="contained"
          color="error"
          onClick={() => void handleSubmit()}
          disabled={isPending}
        >
          {isPending ? t('common.saving') : t(`${prefix}.submit`)}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
